import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { api, formatApiError } from "@/lib/api";
import { refreshAdminSettings } from "@/lib/useAdminSettings";
import { useSiteData } from "@/context/SiteDataContext";
import { PageHeader, Panel } from "@/components/admin/common";

const FIELDS = [
  ["company_name", "Company name"], ["website", "Website"], ["whatsapp", "WhatsApp number (with country code)"],
  ["phone", "Office phone"], ["email", "Public email"], ["office_hours", "Office hours"],
];

const PAY_FIELDS = [
  ["upi_id", "UPI ID", "yourname@bank"], ["bank_account_name", "Account holder name", ""], ["bank_name", "Bank name", ""],
  ["bank_account_number", "Account number", ""], ["bank_ifsc", "IFSC code", "ABCD0123456"], ["bank_branch", "Branch", ""],
];

const Section = ({ title, hint, children }) => (
  <div className="sm:col-span-2 pt-4 first:pt-0">
    <p className="font-semibold text-navy">{title}</p>
    {hint && <p className="text-xs text-slate-500 mt-0.5">{hint}</p>}
    <div className="mt-4 grid gap-4 sm:grid-cols-2">{children}</div>
  </div>
);

export default function Settings() {
  const { setSettings } = useSiteData();
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/settings").then((r) => setF(r.data)); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings", f);
      setF(data);
      refreshAdminSettings(data);
      setSettings((s) => ({ ...s, ...data }));
      toast.success("Company details saved");
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  if (!f) return <Loader2 className="h-6 w-6 animate-spin text-ocean" />;
  return (
    <div data-testid="admin-settings-page">
      <PageHeader title="Company settings" subtitle="Shown on the Contact page, footer and PDF quotations. Leave a field blank to hide it from the website." />
      <Panel className="p-6 max-w-3xl">
        <form onSubmit={save} className="grid gap-4 sm:grid-cols-2 divide-y divide-slate-100">
          <Section title="Company & contact" hint="Shown on the Contact page, footer and PDFs. Leave blank to hide.">
          {FIELDS.map(([k, l]) => (
            <div key={k} className="space-y-1.5"><Label>{l}</Label><Input value={f[k] || ""} onChange={(e) => setF((s) => ({ ...s, [k]: e.target.value }))} data-testid={`settings-${k}-input`} /></div>
          ))}
          <div className="space-y-1.5 sm:col-span-2"><Label>Office address</Label><Textarea rows={3} value={f.address || ""} onChange={(e) => setF((s) => ({ ...s, address: e.target.value }))} data-testid="settings-address-input" /></div>
          </Section>
          <Section title="Payment details" hint="Printed on quotations, booking confirmations and receipts (with a UPI QR code) and added to WhatsApp payment reminders. Never shown on the public website.">
            {PAY_FIELDS.map(([k, l, ph]) => (
              <div key={k} className="space-y-1.5"><Label>{l}</Label><Input value={f[k] || ""} placeholder={ph} onChange={(e) => setF((s) => ({ ...s, [k]: e.target.value }))} data-testid={`settings-${k}-input`} /></div>
            ))}
            <div className="space-y-1.5 sm:col-span-2"><Label>Payment note (optional)</Label><Input value={f.payment_note || ""} placeholder="e.g. Please share the payment screenshot on WhatsApp" onChange={(e) => setF((s) => ({ ...s, payment_note: e.target.value }))} data-testid="settings-payment_note-input" /></div>
          </Section>
          <Section title="Guest feedback" hint="Google review link sent in the thank-you message 1 day after each trip.">
            <div className="space-y-1.5 sm:col-span-2"><Label>Google review link</Label><Input value={f.review_link || ""} placeholder="https://g.page/r/.../review" onChange={(e) => setF((s) => ({ ...s, review_link: e.target.value }))} data-testid="settings-review_link-input" /></div>
            {[["instagram_url", "Instagram profile link"], ["facebook_url", "Facebook page link"], ["youtube_url", "YouTube channel link (optional)"]].map(([k, label]) => (
              <div key={k} className="space-y-1.5 sm:col-span-2"><Label>{label}</Label><Input value={f[k] || ""} placeholder="https://..." onChange={(e) => setF((s) => ({ ...s, [k]: e.target.value }))} data-testid={`settings-${k}-input`} /></div>
            ))}
          </Section>
          <Section title="Lead follow-ups" hint="Every morning at 9am IST you get an email (and a WhatsApp message, once WhatsApp is connected and the owt_followup_digest template is approved) listing the lead follow-ups due that day.">
            <div className="space-y-1.5"><Label>Owner WhatsApp for alerts</Label><Input value={f.owner_alert_whatsapp || ""} placeholder="+91 98765 43210" onChange={(e) => setF((s) => ({ ...s, owner_alert_whatsapp: e.target.value }))} data-testid="settings-owner_alert_whatsapp-input" />
              <p className="text-xs text-slate-500">Use a personal number, not the business number that sends the messages.</p></div>
            <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 p-3">
              <span className="text-sm text-slate-700">Send daily follow-up digest</span>
              <Switch checked={!!f.auto_followup_digest} onCheckedChange={(v) => setF((s) => ({ ...s, auto_followup_digest: v }))} data-testid="settings-auto_followup_digest-switch" />
            </div>
            <Button type="button" variant="outline" className="sm:col-span-2 justify-self-start" data-testid="settings-send-digest-button"
              onClick={() => api.post("/admin/crm/digest").then(() => toast.success("Digest is being sent if any follow-ups are due today"))}>Send today's digest now</Button>
          </Section>
          <Section title="Trip briefing checklist" hint="One item per line. Included as 'What to pack' in every WhatsApp trip briefing.">
            <div className="space-y-1.5 sm:col-span-2"><Textarea rows={8} value={f.packing_checklist || ""} onChange={(e) => setF((s) => ({ ...s, packing_checklist: e.target.value }))} data-testid="settings-packing_checklist-input" /></div>
          </Section>
          <Button type="submit" disabled={saving} className="sm:col-span-2 h-11 bg-ocean hover:bg-navy" data-testid="settings-save-button">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save details"}
          </Button>
        </form>
      </Panel>
    </div>
  );
}
